import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PageTransition from '../components/PageTransition';
import { getServiceRequests } from '../services/serviceRequestService';
import { getOrders } from '../services/sparePartsService';
import { useMenu } from '../contexts/MenuContext';
import { applyPendingAccountUpdates, incrementAccountCacheCount, decrementAccountCacheCount } from '../utils/accountCacheUtils';
import { useRipple } from '../hooks/useRipple';
import PasswordChangeModal from '../components/PasswordChangeModal';
import UserInfoModal from '../components/UserInfoModal';
import Toast from '../components/Toast';
import { useScrollPrevention } from '../hooks/useScrollPrevention';

interface StatCard {
  id: string;
  title: string | React.ReactNode;
  description: string;
  link: string;
  gradient: string;
  iconPath: string;
  textColor: string;
  total: number;
  order: number;
  delay: number;
}

// Memoized StatCard Component
const StatCardComponent = memo(({ 
  card, 
  loading, 
  onCardClick, 
  ripples,
  isMobile 
}: { 
  card: StatCard; 
  loading: boolean; 
  onCardClick: (cardId: string, link: string) => (event: React.MouseEvent) => void;
  ripples: any;
  isMobile: boolean;
}) => (
  <motion.div
    key={card.id}
    initial={{ opacity: 0, y: 30, scale: 0.9 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ 
      duration: 0.5, 
      delay: card.delay,
      type: "spring",
      stiffness: 100
    }}
    className={`${
      card.id === 'pending-requests' ? 'lg:order-1' :
      card.id === 'approved-requests' ? 'lg:order-2' :
      card.id === 'cancelled-requests' ? 'lg:order-3' :
      card.id === 'pending-orders' ? 'lg:order-4' :
      card.id === 'approved-orders' ? 'lg:order-5' :
      card.id === 'cancelled-orders' ? 'lg:order-6' :
      ''
    }`}
    whileHover={isMobile ? {} : { 
      y: -4,
      transition: { duration: 0.2 }
    }}
    whileTap={isMobile ? {} : { scale: 0.98 }}
  >
    <div
      onClick={onCardClick(card.id, card.link)}
      className={`group relative cursor-pointer border rounded-2xl ${card.gradient} ${isMobile ? '' : 'transition-transform duration-300 hover:shadow-lg hover:shadow-black/5 dark:hover:shadow-black/20'} overflow-hidden`}
    >
      <div className="rounded-2xl p-3 lg:p-4 border border-white dark:border-gray-700">
      {/* Ripple Effects */}
      {ripples[card.id]?.map((ripple: any) => (
        <motion.div
          key={ripple.id}
          className={`absolute rounded-full pointer-events-none ${
            card.id.includes('pending') ? 'bg-amber-400/40' :
            card.id.includes('approved') ? 
              (card.id.includes('requests') ? 'bg-emerald-400/40' : 'bg-teal-400/40') :
            card.id.includes('cancelled') ?
              (card.id.includes('requests') ? 'bg-red-400/40' : 'bg-rose-400/40') :
            'bg-indigo-400/40'
          }`}
          style={{
            left: ripple.x - (ripple.size || 50) / 2,
            top: ripple.y - (ripple.size || 50) / 2,
            width: ripple.size || 50,
            height: ripple.size || 50,
          }}
          initial={{ scale: 0.1, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
      ))}

      {/* Hover Light Flow Effect */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
        <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 ${isMobile ? '' : 'transition-opacity duration-300'}`}>
          <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 -translate-x-full ${isMobile ? '' : 'group-hover:translate-x-full transition-transform duration-700 ease-out'}`}></div>
        </div>
      </div>

      {/* Background Pattern */}
      <div className="absolute top-0 right-0 w-20 h-20 opacity-5">
        <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d={card.iconPath} />
        </svg>
      </div>
      
      <div className="relative">
        {/* Status Badge */}
        <div className="flex items-center mb-2">
          <div className={`${card.textColor} p-1.5 rounded-lg bg-white/40 dark:bg-black/20 backdrop-blur-sm`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={card.iconPath} />
            </svg>
          </div>
          <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${card.textColor} bg-white/30 dark:bg-black/20`}>
            {card.description}
          </span>
        </div>
        
        {/* Number Display */}
        <div className="mb-2 h-10 lg:h-12 ml-4">
          {loading ? (
            <div className="space-y-1">
              <div className={`animate-pulse h-7 w-14 rounded ${
                card.id.includes('pending') ? 'bg-amber-300/70 dark:bg-amber-700/60' :
                card.id.includes('approved') ? 
                  (card.id.includes('requests') ? 'bg-emerald-300/70 dark:bg-emerald-700/60' : 'bg-teal-300/70 dark:bg-teal-700/60') :
                card.id.includes('cancelled') ?
                  (card.id.includes('requests') ? 'bg-red-300/70 dark:bg-red-700/60' : 'bg-rose-300/70 dark:bg-rose-700/60') :
                'bg-gray-300 dark:bg-gray-600'
              }`}></div>
              <div className={`animate-pulse h-2 w-20 rounded ${
                card.id.includes('pending') ? 'bg-amber-300/50 dark:bg-amber-700/40' :
                card.id.includes('approved') ? 
                  (card.id.includes('requests') ? 'bg-emerald-300/50 dark:bg-emerald-700/40' : 'bg-teal-300/50 dark:bg-teal-700/40') :
                card.id.includes('cancelled') ?
                  (card.id.includes('requests') ? 'bg-red-300/50 dark:bg-red-700/40' : 'bg-rose-300/50 dark:bg-rose-700/40') :
                'bg-gray-300 dark:bg-gray-600'
              }`}></div>
            </div>
          ) : (
            <motion.div 
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: card.delay + 0.2 }}
            >
              <div className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-1 tracking-tight">
                {card.total.toLocaleString('tr-TR')}
              </div>
              <div className="h-0.5 w-10 bg-gradient-to-r from-current to-transparent opacity-30 rounded-full"></div>
            </motion.div>
          )}
        </div>
        
        {/* Title */}
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 leading-tight">
          {card.title}
        </h3>
        
        {/* Hover Arrow */}
        <div className={`absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 ${isMobile ? '' : 'transition-transform duration-200'} transform translate-x-1 ${isMobile ? '' : 'group-hover:translate-x-0'}`}>
          <div className={`${card.textColor} p-1`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
      </div>
    </div>
  </motion.div>
));

export default function Account() {
  const navigate = useNavigate();
  const { isMenuButtonVisible } = useMenu();
  const [stats, setStats] = useState({
    pendingTotal: 0,
    approvedTotal: 0,
    cancelledTotal: 0,
    ordersPendingTotal: 0,
    ordersApprovedTotal: 0,
    ordersCancelledTotal: 0
  });
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Ripple hook
  const { ripples, createRipple } = useRipple<string>();

  // Toast state (birleştirildi)
  const [toast, setToast] = useState({
    message: '',
    type: 'success' as 'success' | 'error',
    isVisible: false
  });

  // Modal states (birleştirildi)
  const [modals, setModals] = useState({
    password: false,
    userInfo: false,
    userDataLoading: false,
    userInfoModalOpened: false
  });
  
  // UserInfoModal için scroll prevention (loading + modal açık olduğu sürece)
  useScrollPrevention(modals.userDataLoading || modals.userInfo);

  // Cache için anahtar ve süre (1 dakika = 60000ms)
  const CACHE_KEY = 'account-stats-cache';
  const CACHE_DURATION = 60000 * 5;

  // Cache yardımcı fonksiyonları (optimize edildi)
  const getCachedData = useCallback(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();
        if (now - timestamp < CACHE_DURATION) {
          return data;
        }
        // Cache süresi dolmuş, temizle
        localStorage.removeItem(CACHE_KEY);
      }
    } catch (error) {
      console.error('Cache okuma hatası:', error);
      localStorage.removeItem(CACHE_KEY);
    }
    return null;
  }, [CACHE_KEY, CACHE_DURATION]);

  const setCachedData = useCallback((data: typeof stats) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Cache yazma hatası:', error);
    }
  }, [CACHE_KEY]);



  // Cache'deki bekleyen miktarları artır (dependency düzeltildi)
  const incrementPendingCounts = useCallback((type: 'request' | 'order') => {
    incrementAccountCacheCount(type, loading, setStats, setCachedData);
  }, [loading, setCachedData]);

  // Cache'deki bekleyen miktarları azalt (dependency düzeltildi)
  const decrementPendingCounts = useCallback((type: 'request' | 'order') => {
    decrementAccountCacheCount(type, loading, setStats, setCachedData);
  }, [loading, setCachedData]);



  // Mobil durumu takibi
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        
        // Önce cache'i kontrol et
        const cachedData = getCachedData();
        if (cachedData) {
          setStats(cachedData);
          setLoading(false);
          return;
        }
        
        // Cache yoksa API'den çek
        const [
          pendingResponse, 
          approvedResponse, 
          cancelledResponse,
          ordersPendingResponse,
          ordersApprovedResponse,
          ordersCancelledResponse
        ] = await Promise.all([
          // Talep istatistikleri
          getServiceRequests(1, 1, [{"type":"list","value":["Onay Bekliyor"],"field":"IslemAdi"}]), // Bekleyen Talepler
          getServiceRequests(1, 1, [{"type":"list","value":["Onaylandı"],"field":"IslemAdi"}]), // Onaylı Talepler
          getServiceRequests(1, 1, [{"type":"list","value":["İptal Edildi"],"field":"IslemAdi"}]), // İptal Edilen Talepler
          // Sipariş istatistikleri
          getOrders(1, 1, [{"type":"list","value":["Onay Bekliyor"],"field":"IslemAdi"}]), // Bekleyen Siparişler
          getOrders(1, 1, [{"type":"list","value":["Onaylandı"],"field":"IslemAdi"}]), // Onaylı Siparişler
          getOrders(1, 1, [{"type":"list","value":["İptal Edildi"],"field":"IslemAdi"}]) // İptal Edilen Siparişler
        ]);

        const newStats = {
          pendingTotal: pendingResponse.total || 0,
          approvedTotal: approvedResponse.total || 0,
          cancelledTotal: cancelledResponse.total || 0,
          ordersPendingTotal: ordersPendingResponse.total || 0,
          ordersApprovedTotal: ordersApprovedResponse.total || 0,
          ordersCancelledTotal: ordersCancelledResponse.total || 0
        };

        setStats(newStats);
        // Yeni veriyi cache'le
        setCachedData(newStats);
        
        // Cache'den yüklendiyse pending updates'leri uygula
        if (cachedData) {
          setTimeout(() => applyPendingAccountUpdates(newStats, setStats, setCachedData), 100);
        }
      } catch (error) {
        console.error('İstatistikler yüklenirken hata:', error);
        // Hata durumunda 0 değerlerde kalsın
      } finally {
        setLoading(false);
        
        // API'den yüklendiyse de pending updates'leri uygula
        setTimeout(() => applyPendingAccountUpdates(stats, setStats, setCachedData), 100);
      }
    };

    fetchStats();
  }, []);

  // Global event listener - yeni talep/sipariş eklendiğinde cache'i güncelle
  useEffect(() => {
    const handleNewRequest = () => {
      incrementPendingCounts('request');
    };

    const handleNewOrder = () => {
      incrementPendingCounts('order');
    };

    const handleDeleteRequest = () => {
      decrementPendingCounts('request');
    };

    const handleDeleteOrder = () => {
      decrementPendingCounts('order');
    };

    // Event listener'ları ekle
    window.addEventListener('newServiceRequestCreated', handleNewRequest);
    window.addEventListener('newOrderCreated', handleNewOrder);
    window.addEventListener('serviceRequestDeleted', handleDeleteRequest);
    window.addEventListener('orderDeleted', handleDeleteOrder);

    // Cleanup
    return () => {
      window.removeEventListener('newServiceRequestCreated', handleNewRequest);
      window.removeEventListener('newOrderCreated', handleNewOrder);
      window.removeEventListener('serviceRequestDeleted', handleDeleteRequest);
      window.removeEventListener('orderDeleted', handleDeleteOrder);
    };
  }, [incrementPendingCounts, decrementPendingCounts]);

  // Kart verilerini tanımla (memoize edildi)
  const statCards: StatCard[] = useMemo(() => [
    {
      id: 'pending-requests',
      title: 'Bekleyen Talepler',
      description: 'Onay bekliyor',
      link: '/service-requests?statuses=2',
              gradient: 'bg-gradient-to-br from-amber-100 to-amber-200/70 hover:from-amber-200 hover:to-amber-300/70 border-amber-300 dark:from-amber-950 dark:to-amber-900/50 dark:hover:from-amber-900 dark:hover:to-amber-800/50 dark:border-amber-800',
      iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
      textColor: 'text-amber-600 dark:text-amber-400',
      total: stats.pendingTotal,
      order: 1,
      delay: 0.1
    },
    {
      id: 'pending-orders',
      title: 'Bekleyen Siparişler',
      description: 'Onay bekliyor',
      link: '/orders?statuses=2',
              gradient: 'bg-gradient-to-br from-orange-100 to-orange-200/70 hover:from-orange-200 hover:to-orange-300/70 border-orange-300 dark:from-orange-950 dark:to-orange-900/50 dark:hover:from-orange-900 dark:hover:to-orange-800/50 dark:border-orange-800',
      iconPath: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z',
      textColor: 'text-orange-600 dark:text-orange-400',
      total: stats.ordersPendingTotal,
      order: 4,
      delay: 0.2
    },
    {
      id: 'approved-requests',
      title: 'Onaylı Talepler',
      description: 'Onaylandı',
      link: '/service-requests?statuses=3',
              gradient: 'bg-gradient-to-br from-emerald-100 to-emerald-200/70 hover:from-emerald-200 hover:to-emerald-300/70 border-emerald-300 dark:from-emerald-950 dark:to-emerald-900/50 dark:hover:from-emerald-900 dark:hover:to-emerald-800/50 dark:border-emerald-800',
      iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      textColor: 'text-emerald-600 dark:text-emerald-400',
      total: stats.approvedTotal,
      order: 2,
      delay: 0.3
    },
    {
      id: 'approved-orders',
      title: 'Onaylı Siparişler',
      description: 'Onaylandı',
      link: '/orders?statuses=3',
              gradient: 'bg-gradient-to-br from-teal-100 to-teal-200/70 hover:from-teal-200 hover:to-teal-300/70 border-teal-300 dark:from-teal-950 dark:to-teal-900/50 dark:hover:from-teal-900 dark:hover:to-teal-800/50 dark:border-teal-800',
      iconPath: 'M5 8l6 6L19 6',
      textColor: 'text-teal-600 dark:text-teal-400',
      total: stats.ordersApprovedTotal,
      order: 5,
      delay: 0.4
    },
    {
      id: 'cancelled-requests',
      title: (
        <>
          <span className="sm:hidden">İptal Talepler</span>
          <span className="hidden sm:inline">İptal Edilen Talepler</span>
        </>
      ),
      description: 'İptal edildi',
      link: '/service-requests?statuses=4',
              gradient: 'bg-gradient-to-br from-red-100 to-red-200/70 hover:from-red-200 hover:to-red-300/70 border-red-300 dark:from-red-950 dark:to-red-900/50 dark:hover:from-red-900 dark:hover:to-red-800/50 dark:border-red-800',
      iconPath: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
      textColor: 'text-red-600 dark:text-red-400',
      total: stats.cancelledTotal,
      order: 3,
      delay: 0.5
    },
    {
      id: 'cancelled-orders',
      title: (
        <>
          <span className="sm:hidden">İptal Siparişler</span>
          <span className="hidden sm:inline">İptal Edilen Siparişler</span>
        </>
      ),
      description: 'İptal edildi',
      link: '/orders?statuses=4',
              gradient: 'bg-gradient-to-br from-rose-100 to-rose-200/70 hover:from-rose-200 hover:to-rose-300/70 border-rose-300 dark:from-rose-950 dark:to-rose-900/50 dark:hover:from-rose-900 dark:hover:to-rose-800/50 dark:border-rose-800',
      iconPath: 'M6 18L18 6M6 6l12 12',
      textColor: 'text-rose-600 dark:text-rose-400',
      total: stats.ordersCancelledTotal,
      order: 6,
      delay: 0.6
    }
  ], [stats]);

  // Toast helper functions (optimize edildi)
  const showSuccessToast = useCallback((message: string) => {
    setToast({ message, type: 'success', isVisible: true });
  }, []);

  const showErrorToast = useCallback((message: string) => {
    setToast({ message, type: 'error', isVisible: true });
  }, []);

  // Card click handler (optimize edildi)
  const handleCardClick = useCallback((cardId: string, link: string) => {
    return (event: React.MouseEvent) => {
      createRipple(event, cardId, () => {
        navigate(link);
      });
    };
  }, [createRipple, navigate]);

  // Modal handlers (optimize edildi)
  const handlePasswordModalOpen = useCallback(() => {
    setModals(prev => ({ ...prev, password: true }));
  }, []);

  const handlePasswordModalClose = useCallback(() => {
    setModals(prev => ({ ...prev, password: false }));
  }, []);

  const handleUserInfoModalOpen = useCallback(() => {
    if (!modals.userDataLoading && !modals.userInfo) {
      setModals(prev => ({ ...prev, userDataLoading: true, userInfo: true }));
    }
  }, [modals.userDataLoading, modals.userInfo]);

  const handleUserInfoModalClose = useCallback(() => {
    setModals({ password: false, userInfo: false, userDataLoading: false, userInfoModalOpened: false });
  }, []);

  return (
    <PageTransition>
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:gap-3">
              <div className="flex items-center gap-3">
                <div className="hidden md:flex w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <h1 className={`text-2xl font-bold text-gray-900 dark:text-white transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    Hesabım
                  </h1>
                  <p className={`text-sm text-gray-600 dark:text-gray-400 mt-1 transition-all duration-300 ease-in-out md:ml-0 ${isMenuButtonVisible ? 'ml-12' : 'ml-0'}`}>
                    Hesabınızı görüntüleyin ve yönetin
                  </p>
                </div>
              </div>
            </div>
            <div className="ml-auto flex items-center gap-3 hidden">
              {/* Güncelle Düğmesi */}
              <button
                onClick={async () => {
                  // Cache'i temizle
                  localStorage.removeItem(CACHE_KEY);
                  
                  // Loading state'ini aktif et
                  setLoading(true);
                  
                  try {
                    // API'den fresh veri çek
                    const [
                      pendingResponse, 
                      approvedResponse, 
                      cancelledResponse,
                      ordersPendingResponse,
                      ordersApprovedResponse,
                      ordersCancelledResponse
                    ] = await Promise.all([
                      getServiceRequests(1, 1, [{"type":"list","value":["Onay Bekliyor"],"field":"IslemAdi"}]),
                      getServiceRequests(1, 1, [{"type":"list","value":["Onaylandı"],"field":"IslemAdi"}]),
                      getServiceRequests(1, 1, [{"type":"list","value":["İptal Edildi"],"field":"IslemAdi"}]),
                      getOrders(1, 1, [{"type":"list","value":["Onay Bekliyor"],"field":"IslemAdi"}]),
                      getOrders(1, 1, [{"type":"list","value":["Onaylandı"],"field":"IslemAdi"}]),
                      getOrders(1, 1, [{"type":"list","value":["İptal Edildi"],"field":"IslemAdi"}])
                    ]);

                    const newStats = {
                      pendingTotal: pendingResponse.total || 0,
                      approvedTotal: approvedResponse.total || 0,
                      cancelledTotal: cancelledResponse.total || 0,
                      ordersPendingTotal: ordersPendingResponse.total || 0,
                      ordersApprovedTotal: ordersApprovedResponse.total || 0,
                      ordersCancelledTotal: ordersCancelledResponse.total || 0
                    };

                    setStats(newStats);
                    setCachedData(newStats);
                  } catch (error) {
                    console.error('Veriler güncellenirken hata:', error);
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className={`group flex items-center gap-1.5 px-3 py-1.5 text-blue-600 dark:text-blue-400 hover:text-white hover:bg-blue-600 dark:hover:bg-blue-600 rounded-md ${isMobile ? '' : 'transition-transform duration-200 hover:shadow-md'} border border-blue-300 dark:border-blue-700 hover:border-blue-600 dark:hover:border-blue-600 shadow-sm font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <svg className={`w-3.5 h-3.5 ${isMobile ? '' : 'transition-transform duration-200 group-hover:scale-110'} ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Güncelle</span>
              </button>
            </div>
          </div>
        </div>

        {/* İstatistik Kartları */}
        <div className="mb-8">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {statCards.map((card) => (
              <StatCardComponent
                key={card.id}
                card={card}
                loading={loading}
                onCardClick={handleCardClick}
                ripples={ripples}
                isMobile={isMobile}
              />
            ))}
          </div>
        </div>

        <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 shadow-lg hover:shadow-xl transition-all duration-200">
          <div className="rounded-xl p-6 sm:p-8 border border-white dark:border-gray-700">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              Hesap Bilgileri
            </h2>
            <div className="w-12 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 gap-6">
            {/* Parola */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div
                onClick={(event) => {
                  createRipple(event, 'password', handlePasswordModalOpen);
                }}
                className={`group relative cursor-pointer border border-blue-200/60 dark:border-gray-600/60 rounded-2xl bg-gradient-to-br from-blue-50/80 via-indigo-50/60 to-purple-50/80 dark:from-gray-800/80 dark:via-gray-750/60 dark:to-gray-800/80 backdrop-blur-sm hover:from-blue-100/90 hover:via-indigo-100/70 hover:to-purple-100/90 dark:hover:from-gray-700/90 dark:hover:via-gray-650/70 dark:hover:to-gray-700/90 ${isMobile ? '' : 'transition-transform duration-300 hover:shadow-2xl hover:scale-[1.02]'} shadow-lg overflow-hidden`}
              >
                <div className="rounded-2xl p-6 border border-white dark:border-gray-700">
                {/* Ripple Effects */}
                {ripples['password']?.map((ripple) => (
                  <motion.div
                    key={ripple.id}
                    className="absolute rounded-full bg-blue-500/20 pointer-events-none"
                    style={{
                      left: ripple.x - (ripple.size || 50) / 2,
                      top: ripple.y - (ripple.size || 50) / 2,
                      width: ripple.size || 50,
                      height: ripple.size || 50,
                    }}
                    initial={{ scale: 0.1, opacity: 0.6 }}
                    animate={{ scale: 1, opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                ))}
                
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5 dark:opacity-10">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full blur-3xl"></div>
                </div>
                
                <div className="relative flex items-start md:items-center space-x-4">
                  <div className={`flex-shrink-0 p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg ${isMobile ? '' : 'group-hover:shadow-xl group-hover:scale-110 transition-transform duration-300'}`}>
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className={`text-xl font-bold text-gray-800 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300 ${isMobile ? '' : 'transition-colors duration-300'}`}>
                        Parola
                      </h3>
                      <div className={`flex-shrink-0 text-blue-500 group-hover:text-blue-700 dark:group-hover:text-blue-300 group-hover:translate-x-1 ${isMobile ? '' : 'transition-transform duration-300'} mt-0.5`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                      <span className="sm:hidden">Şifrenizi değiştirin</span>
                      <span className="hidden sm:inline">Şifrenizi değiştirin ve hesap güvenliğinizi artırın</span>
                    </p>
                  </div>
                </div>
                </div>
              </div>
            </motion.div>
            
            {/* Kullanıcı Bilgileri */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div
                onClick={(event) => {
                  createRipple(event, 'user', handleUserInfoModalOpen);
                }}
                className={`group relative cursor-pointer border border-orange-200/60 dark:border-gray-600/60 rounded-2xl bg-gradient-to-br from-orange-50/80 via-amber-50/60 to-yellow-50/80 dark:from-gray-800/80 dark:via-gray-750/60 dark:to-gray-800/80 backdrop-blur-sm hover:from-orange-100/90 hover:via-amber-100/70 hover:to-yellow-100/90 dark:hover:from-gray-700/90 dark:hover:via-gray-650/70 dark:hover:to-gray-700/90 ${isMobile ? '' : 'transition-transform duration-300 hover:shadow-2xl hover:scale-[1.02]'} shadow-lg overflow-hidden`}
              >
                <div className="rounded-2xl p-6 border border-white dark:border-gray-700">
                {/* Ripple Effects */}
                {ripples['user']?.map((ripple) => (
                  <motion.div
                    key={ripple.id}
                    className="absolute rounded-full bg-orange-500/20 pointer-events-none"
                    style={{
                      left: ripple.x - (ripple.size || 50) / 2,
                      top: ripple.y - (ripple.size || 50) / 2,
                      width: ripple.size || 50,
                      height: ripple.size || 50,
                    }}
                    initial={{ scale: 0.1, opacity: 0.6 }}
                    animate={{ scale: 1, opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                ))}
                
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5 dark:opacity-10">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full blur-3xl"></div>
                </div>
                
                <div className="relative flex items-start md:items-center space-x-4">
                  <div className={`flex-shrink-0 p-3 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl shadow-lg ${isMobile ? '' : 'group-hover:shadow-xl group-hover:scale-110 transition-transform duration-300'}`}>
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between mb-1">
                      <h3 className={`text-xl font-bold text-gray-800 dark:text-white group-hover:text-orange-700 dark:group-hover:text-orange-300 ${isMobile ? '' : 'transition-colors duration-300'}`}>
                       <span className="sm:hidden">Kullanıcı</span>
                       <span className="hidden sm:inline">Kullanıcı Bilgileri</span>
                      </h3>
                      <div className={`flex-shrink-0 text-orange-500 group-hover:text-orange-700 dark:group-hover:text-orange-300 group-hover:translate-x-1 ${isMobile ? '' : 'transition-transform duration-300'} mt-0.5`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                       <span className="sm:hidden">Bilgilerinizi güncelleyin</span>
                       <span className="hidden sm:inline">Firma ve iletişim bilgilerinizi görüntüleyin ve güncelleyin</span>
                    </p>
                  </div>
                </div>
                </div>
              </div>
            </motion.div>
            
            {/* Hesap Özeti */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div
                onClick={(event) => {
                  createRipple(event, 'account-summary', () => {
                    navigate('/account-summary');
                  });
                }}
                className={`group relative cursor-pointer border border-emerald-200/60 dark:border-gray-600/60 rounded-2xl bg-gradient-to-br from-emerald-50/80 via-teal-50/60 to-green-50/80 dark:from-gray-800/80 dark:via-gray-750/60 dark:to-gray-800/80 backdrop-blur-sm hover:from-emerald-100/90 hover:via-teal-100/70 hover:to-green-100/90 dark:hover:from-gray-700/90 dark:hover:via-gray-650/70 dark:hover:to-gray-700/90 ${isMobile ? '' : 'transition-transform duration-300 hover:shadow-2xl hover:scale-[1.02]'} shadow-lg overflow-hidden`}
              >
                <div className="rounded-2xl p-6 border border-white dark:border-gray-700">
                {/* Ripple Effects */}
                {ripples['account-summary']?.map((ripple) => (
                  <motion.div
                    key={ripple.id}
                    className="absolute rounded-full bg-emerald-500/20 pointer-events-none"
                    style={{
                      left: ripple.x - (ripple.size || 50) / 2,
                      top: ripple.y - (ripple.size || 50) / 2,
                      width: ripple.size || 50,
                      height: ripple.size || 50,
                    }}
                    initial={{ scale: 0.1, opacity: 0.6 }}
                    animate={{ scale: 1, opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                ))}
                
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5 dark:opacity-10">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full blur-3xl"></div>
                </div>
                
                <div className="relative flex items-start md:items-center space-x-4">
                  <div className={`flex-shrink-0 p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg ${isMobile ? '' : 'group-hover:shadow-xl group-hover:scale-110 transition-transform duration-300'}`}>
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className={`text-xl font-bold text-gray-800 dark:text-white group-hover:text-emerald-700 dark:group-hover:text-emerald-300 ${isMobile ? '' : 'transition-colors duration-300'}`}>
                        Hesap Özeti
                      </h3>
                      <div className={`flex-shrink-0 text-emerald-500 group-hover:text-emerald-700 dark:group-hover:text-emerald-300 group-hover:translate-x-1 ${isMobile ? '' : 'transition-transform duration-300'} mt-0.5`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                      <span className="sm:hidden">İşlemlerinizi görün</span>
                      <span className="hidden sm:inline">İşlem geçmişinizi görün ve mali durumunuzu takip edin</span>
                    </p>
                  </div>
                </div>
                </div>
              </div>
            </motion.div>
            
            {/* Duyurular */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div
                onClick={(event) => {
                  createRipple(event, 'announcements', () => {
                    navigate('/announcements');
                  });
                }}
                className={`group relative cursor-pointer border border-violet-200/60 dark:border-gray-600/60 rounded-2xl bg-gradient-to-br from-violet-50/80 via-purple-50/60 to-pink-50/80 dark:from-gray-800/80 dark:via-gray-750/60 dark:to-gray-800/80 backdrop-blur-sm hover:from-violet-100/90 hover:via-purple-100/70 hover:to-pink-100/90 dark:hover:from-gray-700/90 dark:hover:via-gray-650/70 dark:hover:to-gray-700/90 ${isMobile ? '' : 'transition-transform duration-300 hover:shadow-2xl hover:scale-[1.02]'} shadow-lg overflow-hidden`}
              >
                <div className="rounded-2xl p-6 border border-white dark:border-gray-700">
                {/* Ripple Effects */}
                {ripples['announcements']?.map((ripple) => (
                  <motion.div
                    key={ripple.id}
                    className="absolute rounded-full bg-violet-500/20 pointer-events-none"
                    style={{
                      left: ripple.x - (ripple.size || 50) / 2,
                      top: ripple.y - (ripple.size || 50) / 2,
                      width: ripple.size || 50,
                      height: ripple.size || 50,
                    }}
                    initial={{ scale: 0.1, opacity: 0.6 }}
                    animate={{ scale: 1, opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                ))}
                
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5 dark:opacity-10">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full blur-3xl"></div>
                </div>
                
                <div className="relative flex items-start md:items-center space-x-4">
                  <div className={`flex-shrink-0 p-3 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow-lg ${isMobile ? '' : 'group-hover:shadow-xl group-hover:scale-110 transition-transform duration-300'}`}>
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className={`text-xl font-bold text-gray-800 dark:text-white group-hover:text-violet-700 dark:group-hover:text-violet-300 ${isMobile ? '' : 'transition-colors duration-300'}`}>
                        Duyurular
                      </h3>
                      <div className={`flex-shrink-0 text-violet-500 group-hover:text-violet-700 dark:group-hover:text-violet-300 group-hover:translate-x-1 ${isMobile ? '' : 'transition-transform duration-300'} mt-0.5`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                      <span className="sm:hidden">Duyuruları görün</span>
                      <span className="hidden sm:inline">Bilgilendirmeleri görün ve gelişmeleri kaçırmayın</span>
                    </p>
                  </div>
                </div>
                </div>
              </div>
            </motion.div>
          </div>
          </div>
        </div>

        {/* Password Change Modal */}
        <PasswordChangeModal
          isOpen={modals.password}
          onClose={handlePasswordModalClose}
          onSuccess={showSuccessToast}
          onError={showErrorToast}
        />

        {/* User Info Modal */}
        <UserInfoModal
          isOpen={modals.userInfo}
          onClose={handleUserInfoModalClose}
          showBackdrop={false}
          onSuccess={(message) => {
            showSuccessToast(message);
            handleUserInfoModalClose();
          }}
          onError={(error) => {
            showErrorToast(error);
            setModals(prev => ({ ...prev, userDataLoading: false }));
            // Hata durumunda modal'ı kapatma - kullanıcı tekrar deneyebilsin
          }}
          onModalOpened={() => {
            setModals(prev => ({ ...prev, userInfoModalOpened: true })); // Spinner'ı gizle
          }}
          onDataLoaded={() => {
            setModals(prev => ({ ...prev, userDataLoading: false })); // Loading overlay'i kapat
          }}
        />

        {/* User Info Loading Overlay */}
        <AnimatePresence>
          {(modals.userDataLoading || modals.userInfo) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-30 flex items-center justify-center bg-black/20 dark:bg-black/40 backdrop-blur-sm"
            >
              <AnimatePresence mode="wait">
                {modals.userDataLoading && !modals.userInfoModalOpened && (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 20 }}
                    transition={{ duration: 0.3, type: "spring", stiffness: 300, damping: 30 }}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 max-w-sm mx-4"
                  >
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mb-4"></div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Kullanıcı Bilgileri Yükleniyor
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                        Bilgileriniz hazırlanıyor, lütfen bekleyiniz...
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Toast */}
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
        />
      </div>
    </PageTransition>
  );
}
